"use client";

import React, { useState, useEffect, useRef } from "react";
import { MapPin, Phone, Mail, Clock, Send, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, Building2 } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : typeof window !== "undefined" ? (window as any).wvcClient : undefined;
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: Array<{
    value: string;
    label: string;
  }>;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id: number;
  key: string;
  name: string;
  description?: string;
  fields: FormFieldDefinition[];
  submit?: {
    label?: string;
    processing_label?: string;
  };
  spam?: {
    token: string;
    min_seconds?: number;
    honeypot?: {
      field: string;
    } | null;
  };
}
export default function Contact() {
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [isLoadingForm, setIsLoadingForm] = useState(true);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [hpValue, setHpValue] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    code?: string;
    message: string;
  }>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationHtml, setConfirmationHtml] = useState<string | null>(null);
  const tokenFetchTimeRef = useRef<number>(Date.now());
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      // Fallback state when wvcClient is unavailable (e.g. preview isolated shell)
      setFormDef({
        id: 1,
        key: "contact_form",
        name: "Formulario de Contacto",
        fields: [{
          id: "f_name",
          key: "nombre",
          type: "text",
          label: "Nombre completo",
          placeholder: "Ej. Lic. Carlos Mendoza",
          required: true
        }, {
          id: "f_email",
          key: "email",
          type: "email",
          label: "Correo electrónico",
          placeholder: "correo@empresa.com",
          required: true
        }, {
          id: "f_phone",
          key: "telefono",
          type: "phone",
          label: "Teléfono",
          placeholder: "(668) 000-0000",
          required: false
        }, {
          id: "f_subject",
          key: "asunto",
          type: "text",
          label: "Asunto",
          placeholder: "Área de interés o consulta",
          required: true
        }, {
          id: "f_message",
          key: "mensaje",
          type: "textarea",
          label: "Mensaje",
          placeholder: "Describa brevemente los requerimientos de su institución o empresa",
          required: true
        }],
        submit: {
          label: "Enviar mensaje",
          processing_label: "Enviando..."
        },
        spam: {
          token: "demo-token",
          min_seconds: 1,
          honeypot: null
        }
      });
      setIsLoadingForm(false);
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/contact_form").then((res: any) => {
      if (!isMounted) return;
      tokenFetchTimeRef.current = Date.now();
      setFormDef(res);
      setIsLoadingForm(false);
    }).catch(async (err: any) => {
      if (!isMounted) return;
      if (err?.status === 404) {
        try {
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const items = listRes?.items || [];
          const matched = items.find((item: any) => item.key === "contact_form") || (items.length === 1 ? items[0] : null) || items.find((item: any) => item.name?.toLowerCase().includes("contacto") || item.name?.toLowerCase().includes("contact"));
          if (matched && isMounted) {
            const retryRes = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
            tokenFetchTimeRef.current = Date.now();
            setFormDef(retryRes);
            setIsLoadingForm(false);
            return;
          }
        } catch {
          // Error handling continued below
        }
      }
      if (isMounted) {
        setIsLoadingForm(false);
        setGeneralError("No fue posible cargar el formulario institucional. Por favor, comuníquese directamente vía telefónica o correo.");
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);
  const handleInputChange = (fieldId: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        return next;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef) return;
    setGeneralError(null);
    setFieldErrors({});
    const minSeconds = formDef.spam?.min_seconds ?? 3;
    const elapsedSeconds = (Date.now() - tokenFetchTimeRef.current) / 1000;
    if (elapsedSeconds < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, (minSeconds - elapsedSeconds + 0.5) * 1000));
    }
    setIsSubmitting(true);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      // Mock local preview submission
      setTimeout(() => {
        setIsSubmitting(false);
        setConfirmationHtml("<p>Gracias por contactarnos. Un especialista de ASV Consultores se comunicará a la brevedad.</p>");
      }, 700);
      return;
    }
    try {
      const payload: any = {
        values: formValues,
        token: formDef.spam?.token || "",
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else {
          setConfirmationHtml(res.confirmation?.message || "<p>Hemos recibido su mensaje satisfactoriamente. Nuestro equipo directivo revisará su solicitud con estricta confidencialidad.</p>");
        }
      } else if (res?.code === "frms_validation_failed") {
        setFieldErrors(res.errors || {});
      } else if (res?.code === "frms_invalid_token") {
        try {
          const fresh = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}`);
          tokenFetchTimeRef.current = Date.now();
          setFormDef(fresh);
          setGeneralError("La sesión del formulario expiró. Por favor, intente enviar nuevamente.");
        } catch {
          setGeneralError("Error de validación de seguridad. Por favor, recargue la página.");
        }
      } else {
        setGeneralError(res?.message || "Ocurrió un error al procesar la solicitud. Por favor intente más tarde.");
      }
    } catch (err: any) {
      setGeneralError(err?.message || "No fue posible conectar con el servidor. Verifique su conexión.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const contactDetails = [{
    icon: MapPin,
    title: "Dirección institucional",
    content: "Av. Rosa Morada 1360, Fracc. Praderas, Los Mochis, Ahome, Sinaloa, México",
    isLink: false
  }, {
    icon: Phone,
    title: "Línea telefónica directa",
    content: "667 250 5982",
    href: "tel:6672505982",
    isLink: true
  }, {
    icon: Mail,
    title: "Correo administrativo",
    content: "javilez@avsconsultores.org",
    href: "mailto:javilez@avsconsultores.org",
    isLink: true
  }, {
    icon: Clock,
    title: "Horario de atención",
    content: "Lunes a Viernes: 9:00 - 18:00",
    isLink: false
  }];
  return <section id="contacto" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 border-t border-border overflow-hidden">
      <div className="container mx-auto px-6 md:px-10 lg:px-16 max-w-7xl">
        {/* Header Briefing */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-muted rounded-xs border border-ring/40 text-foreground font-serif uppercase text-xs tracking-wider mb-4 font-semibold">
            <Building2 className="w-3.5 h-3.5 text-ring" />
            <span>Sede Central &middot; Los Mochis</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold tracking-wide uppercase text-foreground leading-tight">
            Contacto y atención institucional
          </h1>
          <div className="w-12 h-[2px] bg-ring mt-4 mb-4" />
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed font-sans">
            Comuníquese directamente con nuestras oficinas o complete el formulario formal para canalizar su asunto con el área correspondiente: Gobierno, Empresa o Legal.
          </p>
        </motion.div>

        {/* Two-Column Grid Architecture */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Institutional Credentials & Location */}
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-8">
            <div className="bg-card text-card-foreground p-6 md:p-8 rounded-sm border border-border border-t-2 border-t-ring shadow-xs">
              <h2 className="font-serif text-lg font-semibold uppercase tracking-wider text-foreground mb-6">
                Información de correspondencia
              </h2>

              <div className="space-y-6">
                {contactDetails.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="flex items-start gap-4">
                      <div className="p-2.5 rounded-sm bg-background border border-border text-foreground shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-ring" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-serif text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                          {item.title}
                        </p>
                        {item.isLink && item.href ? <Link href={item.href} className="font-sans text-sm md:text-base text-foreground hover:text-ring transition-colors break-words font-medium block">
                            {item.content}
                          </Link> : <p className="font-sans text-sm md:text-base text-foreground leading-relaxed">
                            {item.content}
                          </p>}
                      </div>
                    </div>;
              })}
              </div>
            </div>

            {/* Styled Map Container */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="font-serif text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Ubicación física &middot; Ahome, Sinaloa
                </span>
                <span className="text-xs text-ring font-sans">Google Maps</span>
              </div>
              <div className="relative w-full h-64 md:h-72 rounded-sm border-2 border-ring/40 overflow-hidden shadow-xs bg-muted">
                <iframe title="Ubicación ASV Consultores en Los Mochis" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14396.442371900137!2d-108.9880854!3d25.7909062!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x86bb1734997184b7%3A0x6d092d6e353229b!2sLos%20Mochis%2C%20Sinaloa!5e0!3m2!1ses!2smx!4v1700000000000!5m2!1ses!2smx" className="absolute inset-0 w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
              <p className="text-xs text-muted-foreground font-sans mt-1">
                Atención presencial previa cita confirmada en oficinas centrales.
              </p>
            </div>
          </motion.div>

          {/* Right Column: WVC Form Engine */}
          <motion.div initial={{
          opacity: 0,
          y: 22
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.08
        }} className="lg:col-span-7">
            <div className="bg-card text-card-foreground p-8 md:p-10 rounded-sm border border-border shadow-sm relative">
              {/* Header inside form card */}
              <div className="mb-8">
                <h2 className="font-serif text-xl font-semibold uppercase tracking-wider text-foreground mb-2">
                  Solicitar asesoría o cotización
                </h2>
                <div className="w-8 h-[2px] bg-ring mb-3" />
                <p className="text-sm text-muted-foreground font-sans">
                  Por favor complete los campos requeridos. Toda información compartida está respaldada por estricto secreto profesional y normativo.
                </p>
              </div>

              {isLoadingForm ? <div className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
                  <Loader2 className="w-8 h-8 animate-spin text-ring" />
                  <p className="text-sm font-sans">Cargando protocolo de atención...</p>
                </div> : confirmationHtml ? <div className="py-10">
                  <Alert className="border-ring/50 bg-background text-foreground">
                    <CheckCircle2 className="w-5 h-5 text-ring" />
                    <AlertTitle className="font-serif uppercase tracking-wider text-sm font-semibold">
                      Solicitud recibida
                    </AlertTitle>
                    <AlertDescription className="mt-2 text-foreground font-sans text-sm">
                      <div dangerouslySetInnerHTML={{
                    __html: confirmationHtml
                  }} className="prose prose-sm max-w-none" />
                    </AlertDescription>
                  </Alert>
                  <div className="mt-6 flex justify-end">
                    <Button variant="outline" onClick={() => {
                  setConfirmationHtml(null);
                  setFormValues({});
                }} className="border-border text-foreground hover:bg-muted font-serif text-xs uppercase tracking-wider">
                      Enviar otra consulta
                    </Button>
                  </div>
                </div> : <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Decoy Honeypot */}
                  {formDef?.spam?.honeypot?.field && <input type="text" name={formDef.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true" />}

                  {generalError && <Alert variant="destructive" className="rounded-sm">
                      <AlertCircle className="w-4 h-4" />
                      <AlertTitle className="font-serif uppercase text-xs font-semibold">
                        Aviso
                      </AlertTitle>
                      <AlertDescription className="text-xs">
                        {generalError}
                      </AlertDescription>
                    </Alert>}

                  {/* Form Fields Dynamic Render */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {formDef?.fields.map((field, i) => {
                  const isFullWidth = field.type === "textarea" || field.key.includes("asunto") || field.key.includes("subject");
                  const error = fieldErrors[field.id]?.message;
                  return <div key={field.id} data-index={i} className={isFullWidth ? "col-span-1 md:col-span-2 space-y-2" : "col-span-1 space-y-2"}>
                          <Label htmlFor={field.id} className="font-serif text-xs uppercase tracking-wider font-semibold text-foreground flex items-center justify-between">
                            <span>
                              {field.label}
                              {field.required && <span className="text-destructive ml-1">*</span>}
                            </span>
                          </Label>

                          {field.type === "textarea" ? <Textarea id={field.id} placeholder={field.placeholder || "Escriba su mensaje..."} value={formValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} rows={4} className="rounded-xs border-input bg-background px-3 py-2 text-foreground font-sans text-sm focus-visible:border-ring focus-visible:ring-ring/50" required={field.required} /> : field.type === "select" && field.options ? <select id={field.id} value={formValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className="h-10 w-full rounded-xs border border-input bg-background px-3 py-1 text-sm font-sans text-foreground outline-none focus-visible:border-ring focus-visible:ring-ring/50" required={field.required}>
                              <option value="">Seleccione una opción</option>
                              {field.options.map((opt, optIndex) => <option key={opt.value} data-index={optIndex} value={opt.value}>
                                  {opt.label}
                                </option>)}
                            </select> : <Input id={field.id} type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text"} placeholder={field.placeholder || ""} value={formValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className="h-10 rounded-xs border-input bg-background px-3 text-foreground font-sans text-sm focus-visible:border-ring focus-visible:ring-ring/50" required={field.required} />}

                          {error && <p className="text-xs text-destructive font-sans flex items-center gap-1 mt-1">
                              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                              {error}
                            </p>}
                        </div>;
                })}
                  </div>

                  {/* Submit Action */}
                  <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border mt-6">
                    <p className="text-xs text-muted-foreground font-sans text-center sm:text-left">
                      Campos marcados con <span className="text-destructive">*</span> son requeridos.
                    </p>
                    <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto h-11 px-8 bg-primary text-primary-foreground border border-ring font-serif text-xs font-semibold uppercase tracking-widest hover:bg-ring hover:text-foreground transition-colors shadow-xs">
                      {isSubmitting ? <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          <span>{formDef?.submit?.processing_label || "Enviando..."}</span>
                        </> : <>
                          <span>{formDef?.submit?.label || "Enviar mensaje"}</span>
                          <Send className="w-3.5 h-3.5 ml-2" />
                        </>}
                    </Button>
                  </div>
                </form>}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}